import {useDisclosure} from '@mantine/hooks';
import type {AppError} from '@packages/utils';
import {useRouter} from 'next/router';
import {parseCookies} from 'nookies';
import {useState} from 'react';
import {
  type ILogin,
  useLogin,
} from '../../../core/repositories/loginRepository';
import {
  setAccessTokenToCookie,
  setRefreshTokenToCookie,
} from '../../../core/usecases/auth/setOauthTokenToCookie';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../shared/components/SnackBar';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';

type CastedError = {message: AppError};

type UseHandleLoginProps = {
  nextRedirectUrl: string | undefined;
} & ILogin;

export const useHandleLogin = ({
  email,
  password,
  version,
  nextRedirectUrl,
}: UseHandleLoginProps) => {
  const isWebView = useIsAppWebView();
  const router = useRouter();
  const [isLoading, handlers] = useDisclosure(false);
  const [hasError, setHasError] = useState(false);

  const cookies = parseCookies();
  const skillCheckHistoryId = cookies.SkillCheckHistoryID ?? '';
  const entrysheetBuilderId = cookies.entrysheetBuilderId ?? '';

  const login = useLogin({
    email,
    password,
    skillCheckHistoryId,
    entrysheetBuilderId,
    version,
    onMutateFnc() {
      handlers.open();
    },
    onSuccessFnc(data) {
      setAccessTokenToCookie({accessToken: data.accessToken});
      setRefreshTokenToCookie({refreshToken: data.refreshToken});
      handlers.close();
      showSuccessSnackBar({title: 'ログインしました', message: 'done'});
      setTimeout(async () => {
        if (isWebView) {
          location.href = nextRedirectUrl ? nextRedirectUrl : '/';

          return;
        }

        await (nextRedirectUrl
          ? router.push(nextRedirectUrl)
          : router.push('/'));
      }, 500);
    },
    onErrorFnc(err: unknown) {
      const castedError = err as CastedError;
      showErrorSnackBar({
        title: 'ログインに失敗しました',
        message: 'メールアドレスもしくはパスワードをご確認ください。',
        isNetworkError: castedError.message === 'NetworkError',
      });
      setHasError(true);
    },
    onSettledFnc() {
      handlers.close();
    },
  });

  const submitLogin = (values: ILogin) => {
    login.mutate(
      values && {
        ...values,
      },
    );
  };

  return {
    submitLogin,
    isLoading,
    hasError,
  };
};
